import React, { useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Row,
  Container,
  Button,
  Progress,
} from "reactstrap";
import Dropzone from "react-dropzone";
import axios from "axios";
import { Link } from "react-router-dom";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import "filepond/dist/filepond.min.css";
import { FILE_DOWNLOAD, FILE_UPLOAD_URL } from "ApiService/UrlHelper";
import { LOCAL_STORAGE } from "Constant/constant";

const FileUpload = () => {
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{
    [key: string]: number;
  }>({});
  const [uploadStatus, setUploadStatus] = useState<{ [key: string]: string }>(
    {}
  );

  function formatBytes(bytes: any, decimals = 2) {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  }

  const handleFileUpload = async (file: any) => {
    const formData = new FormData();
    formData.append("excel_file", file);
    try {
      setUploadStatus((prevStatus) => ({
        ...prevStatus,
        [file.name]: "in progress",
      }));

      const response: any = await axios.post(FILE_UPLOAD_URL, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Password-Authentication": true,
          Authorization: "Bearer " + localStorage.getItem(LOCAL_STORAGE.TOKEN),
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1)
          );
          setUploadProgress((prevProgress) => ({
            ...prevProgress,
            [file.name]: percentCompleted,
          }));
        },
      });

      setUploadStatus((prevStatus) => ({
        ...prevStatus,
        [file.name]: "uploaded",
      }));

      if (response && response?.filename) {
        await downloadFile(response?.filename);
        setSelectedFiles([]);
      }
    } catch (error) {
      console.error(`Error uploading ${file.name}:`, error);
      setUploadStatus((prevStatus) => ({
        ...prevStatus,
        [file.name]: "failed",
      }));
    }
  };

  const downloadFile = async (filename: string) => {
    try {
      // First, let's try to fetch the file with fetch API instead of axios
      const response = await fetch(`${FILE_DOWNLOAD}/${filename}`, {
        headers: {
          "Password-Authentication": "true",
          Authorization: `Bearer ${localStorage.getItem(LOCAL_STORAGE.TOKEN)}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // Get the response as blob
      const blob = await response.blob();

      // Create and trigger download
      const url = window.URL.createObjectURL(blob);

      // Create a link element
      const link = document.createElement("a");
      link.href = url;
      link.download = filename.endsWith(".html")
        ? filename
        : `${filename}.html`;

      // Directly click it without appending to document
      link.click();

      // Clean up
      window.URL.revokeObjectURL(url);

      return true; // Indicate success
    } catch (error) {
      console.error("Error in downloadFile function:", error);
      return false; // Indicate failure
    }
  };

  function handleAcceptedFile(file: File) {
    const processedFile = Object.assign(file, {
      formattedSize: formatBytes(file.size),
    });

    setSelectedFiles([processedFile]); // Overwrites instead of appending

    handleFileUpload(processedFile);
  }

  const removeFile = (index: number) => {
    setSelectedFiles((prevFiles) => {
      const updatedFiles = prevFiles.filter((_, i) => i !== index);
      return updatedFiles;
    });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="File Upload" pageTitle="Forms" />
          <Row className="align-items-center justify-content-center">
            <Col lg={6}>
              <Card>
                <CardBody>
                  <Dropzone
                    onDrop={(acceptedFiles: File[]) => {
                      if (acceptedFiles.length > 0) {
                        handleAcceptedFile(acceptedFiles[0]);
                      }
                    }}
                    multiple={false} // Ensures only a single file can be selected
                  >
                    {({ getRootProps, getInputProps }) => (
                      <div
                        className="dropzone dz-clickable"
                        {...getRootProps()}
                      >
                        <input {...getInputProps()} />
                        <div className="dz-message needsclick">
                          <div className="mb-3">
                            <i className="display-4 text-muted ri-upload-cloud-2-fill" />
                          </div>
                          <h4>Drop files here or click to upload.</h4>
                        </div>
                      </div>
                    )}
                  </Dropzone>
                  {Object.values(uploadStatus).includes("uploaded") && (
                    <p className="mt-3">
                      <strong>
                        Uploaded Files:{" "}
                        {
                          selectedFiles.filter(
                            (file) => uploadStatus[file.name] === "uploaded"
                          ).length
                        }
                      </strong>
                    </p>
                  )}
                  <div className="list-unstyled mb-0" id="file-previews">
                    {selectedFiles.length > 0 ? (
                      selectedFiles.map((f: any, i: any) => {
                        const status = uploadStatus[f.name] || "pending";
                        return (
                          <Card
                            className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                            key={i + "-file"}
                          >
                            <div className="p-2">
                              <Row className="align-items-center">
                                <Col>
                                  <Link
                                    to="#"
                                    className="text-muted font-weight-bold"
                                  >
                                    {f.name}
                                  </Link>
                                  <p className="mb-0">
                                    <strong>{f.formattedSize}</strong>
                                  </p>
                                  {uploadProgress[f.name] !== undefined && (
                                    <Progress
                                      value={uploadProgress[f.name]}
                                      color="success"
                                      style={{ marginTop: "5px" }}
                                    >
                                      {uploadProgress[f.name]}%
                                    </Progress>
                                  )}
                                  <p
                                    style={{
                                      color:
                                        status === "uploaded"
                                          ? "green"
                                          : status === "failed"
                                          ? "red"
                                          : "orange",
                                    }}
                                  >
                                    {status === "uploaded"
                                      ? "Uploaded"
                                      : status === "failed"
                                      ? "Failed"
                                      : "In Progress"}
                                  </p>
                                </Col>
                                <Col className="col-auto">
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => removeFile(i)}
                                  >
                                    <i className="ri-close-line"></i>
                                  </Button>
                                </Col>
                              </Row>
                            </div>
                          </Card>
                        );
                      })
                    ) : (
                      <p>No files uploaded yet.</p>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default FileUpload;
